// built-from: /chat-component v3 impl:chat-component role:implementation
// Spec 100, owner decision 7: what a reply captured, named in plain words.
// A fact event carries an id the consultant never chose and never needs to
// read. Its label comes from its own content; the id is only ever a last
// resort, and even then it arrives humanised rather than as raw punctuation.
//
// Pure: no React, no I/O. Tested by utils/__tests__/turnFacts.test.ts.

/** One fact a turn captured, as the reply footer shows it. */
export interface TurnFact {
  factId: string;
  label: string;
}

const MAX_LABEL = 80;

function firstLine(value: string): string {
  const line = value.split(/\r?\n/).find((part) => part.trim().length > 0) ?? '';
  return line.trim();
}

function tidy(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const line = firstLine(value);
  if (!line) return null;
  return line.length > MAX_LABEL ? `${line.slice(0, MAX_LABEL).trimEnd()}...` : line;
}

/** The tail of a fact id, humanised: `engagement.objectives.objective:pace` → `Pace`. */
export function humaniseFactId(factId: string): string {
  const tail = String(factId).split(/[.:/]/).filter(Boolean).pop() || '';
  const words = tail.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!words) return 'Captured detail';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The words for one fact, in the order the owner asked for: what it says,
 * then what it is called, then the opening line of its description, then the
 * humanised tail of its id. A raw id with dots and colons never reaches the
 * screen.
 */
export function factLabel(factId: string, content: unknown): string {
  const fields = content && typeof content === 'object'
    ? content as Record<string, unknown>
    : {};
  return tidy(fields.text)
    ?? tidy(fields.name)
    ?? tidy(fields.title)
    ?? tidy(fields.label)
    ?? tidy(fields.description)
    ?? humaniseFactId(factId);
}

/**
 * The facts one turn captured, in the order they arrived and once each. A
 * later event for the same fact replaces the earlier label, so the line reads
 * as the turn left it.
 */
export function turnFactsFrom(events: unknown): TurnFact[] {
  if (!Array.isArray(events)) return [];
  const byId = new Map<string, TurnFact>();
  for (const event of events) {
    if (!event || typeof event !== 'object') continue;
    const { factId, content } = event as { factId?: unknown; content?: unknown };
    if (typeof factId !== 'string' || !factId) continue;
    byId.set(factId, { factId, label: factLabel(factId, content) });
  }
  return [...byId.values()];
}
